/* eslint-disable no-unused-vars */

import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from "recharts";
import "../styles/VoterInsightsPage.css";
import {
  fetchConcerns,
  fetchLoyalty,
  fetchOnline,
  fetchConsideration,
} from "../api/voterInsight";
const VoterInsightsPage = () => {
  const [loading, setLoading] = useState(true);
  const [loyaltyData, setLoyaltyData] = useState([]);
  const [youthInfluenceData, setYouthInfluenceData] = useState([]);
  const [voterPrioritiesData, setVoterPrioritiesData] = useState([]);
  const [issuesData, setIssuesData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Create a reference to the timeout to clear it later
    let timeoutId = null;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Call the API functions with the correct parameters
        // Based on the implementation, it seems these functions expect parameters
        // even though they're not being used in the actual implementation
        // We'll pass null values to maintain compatibility
        const loyalty = await fetchLoyalty(null, null, null);
        const online = await fetchOnline(null, null, null);
        const consideration = await fetchConsideration(null, null, null);
        const concerns = await fetchConcerns(null, null, null);

        // Update state with the fetched data
        setLoyaltyData(loyalty);
        setYouthInfluenceData(online);
        setVoterPrioritiesData(consideration);
        setIssuesData(concerns);

        console.log(loyalty);
      } catch (err) {
        console.error("Error fetching voter insight data:", err);
        setError("Failed to load voter insight data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    // Cleanup function to clear timeout when component unmounts
    // or when dependencies change
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []); // Add searchTerm as dependency if using search functionality

  const loadMockData = () => {
    // Mock data for development
    const mockLoyaltyData = [
      { year: 2001, "18-24": 32, "65 plus": 60 },
      { year: 2004, "18-24": 35, "65 plus": 60 },
      { year: 2007, "18-24": 39, "65 plus": 53 },
      { year: 2010, "18-24": 34, "65 plus": 63 },
      { year: 2013, "18-24": 36, "65 plus": 59 },
      { year: 2016, "18-24": 22, "65 plus": 53 },
      { year: 2019, "18-24": 54, "65 plus": 44 },
      { year: 2022, "18-24": 25, "65 plus": 44 },
    ];

    const mockYouthInfluenceData = [
      { year: 2007, persuade: 24, unofficial_vid: 19 },
      { year: 2010, persuade: 22, unofficial_vid: 10 },
      { year: 2013, persuade: 18, unofficial_vid: 26 },
      { year: 2016, persuade: 19, unofficial_vid: 32 },
      { year: 2019, persuade: 31, unofficial_vid: 59 },
    ];

    const mockVoterPrioritiesData = [
      {
        year: 1996,
        policy_issues: 0,
        candidates: 7,
        party_leaders: 15,
        parties: 29,
      },
      {
        year: 1998,
        policy_issues: 65,
        candidates: 6,
        party_leaders: 9,
        parties: 20,
      },
      {
        year: 2001,
        policy_issues: 48,
        candidates: 9,
        party_leaders: 18,
        parties: 26,
      },
      {
        year: 2004,
        policy_issues: 49,
        candidates: 6,
        party_leaders: 19,
        parties: 26,
      },
      {
        year: 2007,
        policy_issues: 53,
        candidates: 6,
        party_leaders: 17,
        parties: 26,
      },
      {
        year: 2010,
        policy_issues: 52,
        candidates: 8,
        party_leaders: 15,
        parties: 25,
      },
      {
        year: 2013,
        policy_issues: 54,
        candidates: 7,
        party_leaders: 14,
        parties: 25,
      },
      {
        year: 2016,
        policy_issues: 59,
        candidates: 9,
        party_leaders: 10,
        parties: 22,
      },
      {
        year: 2019,
        policy_issues: 65,
        candidates: 8,
        party_leaders: 7,
        parties: 19,
      },
      {
        year: 2022,
        policy_issues: 53,
        candidates: 11,
        party_leaders: 8,
        parties: 26,
      },
    ];

    const mockIssuesData = [
      { name: "National Security", value: 85, fill: "#4DAF7C" },
      { name: "Welfare", value: 80, fill: "#4DAF7C" },
      { name: "Education", value: 78, fill: "#4DAF7C" },
      { name: "Healthcare", value: 75, fill: "#3E8D66" },
      { name: "Environment", value: 70, fill: "#3E8D66" },
      { name: "Taxation", value: 68, fill: "#347254" },
      { name: "Economy", value: 65, fill: "#347254" },
      { name: "Social Issues", value: 60, fill: "#2B5A45" },
      { name: "Government Trust", value: 50, fill: "#1F402F" },
      { name: "Immigration", value: 45, fill: "#1F402F" },
    ];

    setLoyaltyData(mockLoyaltyData);
    setYouthInfluenceData(mockYouthInfluenceData);
    setVoterPrioritiesData(mockVoterPrioritiesData);
    setIssuesData(mockIssuesData);

    // Set default selected year to the most recent
    setSelectedYear(2022);
  };

  const handleYearFilter = (year) => {
    setSelectedYear(year);
  };

  // Custom tooltip for the line chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-year">{`Year: ${label}`}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {`${entry.name}: ${entry.value.toFixed(1)}%`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return <div className="loading-container">Loading insights data...</div>;
  }

  return (
    <div className="voter-insights-container">
      <header className="insights-header">
        <h1>Voter Behavior Insights</h1>
        <p className="insights-subtitle">
          Understanding voter behavior is crucial for maintaining democratic
          integrity. These visualizations highlight the importance of
          fact-checking tools in elections.
        </p>
      </header>

      {/* Section 1: Voter Loyalty by Age Group */}
      <section className="insights-section">
        <div className="section-header">
          <h2>Party Loyalty by Age Group (2001-2022)</h2>
          <p className="section-subheader">
            Percentage of voters who consistently vote for the same party
          </p>
        </div>

        <div className="chart-container">
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={loyaltyData}
              margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
              barGap={0}
              barCategoryGap="20%"
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis
                label={{
                  value: "Percentage (%)",
                  angle: -90,
                  position: "insideLeft",
                }}
              />
              <Tooltip />
              <Legend />
              <Bar dataKey="18-24" name="Young Voters (18-24)" fill="#5B8FF9" />
              <Bar dataKey="65 plus" name="Older Voters (65+)" fill="#F6BD16" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="insights-box">
          <h3>Key Insights</h3>
          <ul>
            <li>
              Young voters (18-24) traditionally show lower party loyalty
              compared to older (65+) voters
            </li>
            <li>
              This pattern dramatically shifted in 2019, when young voter
              loyalty increased to 54%, surpassing older voters
            </li>
            <li>
              The 2019 spike coincided with the COVID-19 pandemic, which
              significantly changed how political information was consumed
            </li>
            <li>
              During lockdowns, young voters increasingly relied on online and
              unofficial sources for political information
            </li>
            <li>
              By 2022, young voter loyalty returned to pre-pandemic levels,
              suggesting the 2019 spike was likely an anomaly driven by pandemic
              conditions
            </li>
          </ul>
        </div>
      </section>

      {/* Section 2: Youth Influence Trends */}
      <section className="insights-section">
        <div className="section-header">
          <h2>Youth-Driven Online Political Influence (2007-2019)</h2>
          <p className="section-subheader">
            Young voters' reliance on unofficial sources and tendency to
            persuade others
          </p>
        </div>

        <div className="chart-container">
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={youthInfluenceData}
              margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
              barGap={0}
              barCategoryGap="20%"
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis
                label={{
                  value: "Percentage (%)",
                  angle: -90,
                  position: "insideLeft",
                }}
              />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="persuade"
                name="Actively Persuade Others to Vote"
                fill="#8E2043"
              />
              <Bar
                dataKey="unofficial_vid"
                name="Rely on Unofficial Video Sources"
                fill="#D13438"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="insights-box">
          <h3>Key Insights</h3>
          <ul>
            <li>
              Young voters' reliance on unofficial video sources has increased
              dramatically from 19% in 2007 to 59% in 2019
            </li>
            <li>
              The tendency to persuade others to vote has grown alongside the
              consumption of unofficial sources
            </li>
            <li>
              The most significant increase occurred between 2016 and 2019,
              coinciding with the rise of social media as a primary news source
            </li>
            <li>
              During the pandemic, these trends accelerated as in-person
              interactions decreased and online engagement increased
            </li>
            <li>
              The correlation between consuming unofficial sources and actively
              persuading others suggests that information quality directly
              impacts voting behavior
            </li>
          </ul>

          <div className="cta-container">
            <h4>Verify Before You Share</h4>
            <p>
              Help ensure the information you share online is accurate and
              reliable.
            </p>
            <a href="/ask" className="cta-button">
              Try Claim Check Tool <span className="arrow-right">→</span>
            </a>
          </div>
        </div>
      </section>

      {/* Section 3: Voter Priorities Trends */}
      <section className="insights-section">
        <div className="section-header">
          <h2>Voter Decision Factors (1996-2022)</h2>
          <p className="section-subheader">
            How voters prioritize policies, candidates, and parties when making
            decisions
          </p>
        </div>

        <div className="chart-container">
          <ResponsiveContainer width="100%" height={400}>
            <LineChart
              data={voterPrioritiesData}
              margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis
                label={{
                  value: "Importance (%)",
                  angle: -90,
                  position: "insideLeft",
                }}
              />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="policy_issues"
                name="Policy Issues"
                stroke="#0078D7"
                strokeWidth={2}
                dot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="candidates"
                name="Candidates in Electorate"
                stroke="#E81123"
                strokeWidth={2}
                dot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="party_leaders"
                name="Party Leaders"
                stroke="#00CC6A"
                strokeWidth={2}
                dot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="parties"
                name="Parties as a Whole"
                stroke="#F7630C"
                strokeWidth={2}
                dot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="insights-box">
          <h3>Key Insights</h3>
          <ul>
            <li>
              Policy issues have consistently been the primary factor in voting
              decisions, peaking at 66% in 2019
            </li>

            <li>
              This shift towards policy-based voting creates a greater need for
              accurate, impartial information about policy positions
            </li>
          </ul>

          <div className="cta-container">
            <h4>Compare Party Policies Side-by-Side</h4>
            <p>
              Stay informed about where parties stand on the issues that matter
              to you.
            </p>
            <a href="/policy-tracker" className="cta-button">
              Explore Policy Tracker <span className="arrow-right">→</span>
            </a>
          </div>
        </div>
      </section>

      {/* Section 4: Top Voter Issues */}
      <section className="insights-section">
        <div className="section-header">
          <h2>Top Policy Concerns Among Voters (2022)</h2>
          <p className="section-subheader">
            Issues that voters considered most important in the 2022 election
          </p>
        </div>

        <div className="chart-container">
          <ResponsiveContainer width="100%" height={500}>
            <BarChart
              data={issuesData}
              layout="vertical"
              margin={{ top: 20, right: 30, left: 120, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="number"
                domain={[0, 100]}
                label={{
                  value: "Number of Response",
                  position: "insideBottom",
                  offset: -5,
                }}
              />
              <YAxis dataKey="name" type="category" width={120} />
              <Tooltip formatter={(value) => [`${value}`, "Importance"]} />
              <Bar dataKey="value" nameKey="name">
                {issuesData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="insights-box">
          <h3>Key Insights</h3>
          <ul>
            <li>
              Voters in 2022 cared most about national security, welfare, and
              education, while immigration and trust in government ranked
              lowest.
            </li>
          </ul>

          <div className="cta-container">
            <h4>Find Politicians Who Match Your Values</h4>
            <p>
              Answer a few questions to discover which candidates best align
              with your policy priorities.
            </p>
            <a href="/policy-swipe" className="cta-button">
              Try Politician Match <span className="arrow-right">→</span>
            </a>
          </div>
        </div>
      </section>

      <footer className="insights-footer">
        <h2>Why Fact-Checking Tools Matter for Democracy</h2>
        <p>
          Voter behavior has shifted significantly in recent decades, with
          younger demographics turning to unofficial sources, especially during
          crises. As voters prioritize policies over party loyalty,
          fact-checking tools have become essential for navigating the complex
          information landscape, enabling informed decisions on key concerns
          like national security, welfare, and education based on verified
          information rather than partisan narratives.
        </p>

        <div className="cta-container cta-container-large">
          <h3>Explore Our Fact-Checking Tools</h3>
          <div className="cta-grid">
            <a href="/ask" className="cta-card">
              <h4>Claim Check</h4>
              <p>Verify statements from politicians and media</p>
            </a>
            <a href="/policy-tracker" className="cta-card">
              <h4>Policy Tracker</h4>
              <p>Compare party policies on key issues</p>
            </a>
            <a href="/policy-swipe" className="cta-card">
              <h4>Politician Match</h4>
              <p>Find politicians aligned with your priorities</p>
            </a>
          </div>
        </div>
        <div className="data-source">Data updated: May 2025</div>
      </footer>
    </div>
  );
};

export default VoterInsightsPage;
